import React, { useId, useState } from 'react';
import { Alert, Button, TextField } from '@mui/material';

import AlphaMarkdown from 'components/Alpha/AlphaMarkdown';
import { LiveAssistAskView } from 'components/Alpha/LiveAssist/liveAssistReducer';
import LiveAssistSources from 'components/Alpha/LiveAssist/LiveAssistSources';
import { useLiveAssistStyles } from 'components/Alpha/LiveAssist/liveAssistStyles';

export const ASK_EMPTY_TEXT =
  'Ask Alpha about this client, their tickets, or how to fix the issue.';
export const ASK_THINKING_TEXT = 'Alpha is thinking…';
export const ASK_NO_ANSWER_GIVEN_TEXT = 'Alpha had no answer to that.';
export const ASK_MAX_LENGTH = 2000;
export const ASK_EMPTY_ERROR = 'Type a question for Alpha first.';
export const ASK_TOO_LONG_ERROR = 'Questions are limited to 2,000 characters.';

const IME_KEY_CODE = 229;
const OPEN_PHASES: Array<LiveAssistAskView['phase']> = [
  'sending',
  'waiting',
  'streaming',
  'queued',
];

interface Props {
  asks: LiveAssistAskView[];
  onAsk: (question: string, retryId?: string) => void;
  inputRef?: React.Ref<HTMLTextAreaElement>;
  closedText?: string;
}

const AskAnswer: React.FC<{ ask: LiveAssistAskView; onRetry?: () => void }> = ({
  ask,
  onRetry,
}) => {
  const classes = useLiveAssistStyles();
  if (ask.phase === 'failed') {
    return (
      <>
        {ask.answer.trim() !== '' && (
          <div className={classes.answer}>
            <AlphaMarkdown text={ask.answer} />
          </div>
        )}
        <Alert
          severity="error"
          className={classes.askError}
          action={
            onRetry === undefined ? undefined : (
              <Button size="small" className={classes.noticeButton} onClick={onRetry}>
                Try again
              </Button>
            )
          }
        >
          {ask.error}
        </Alert>
      </>
    );
  }
  if (ask.answer === '' && ask.phase !== 'done') {
    return <p className={classes.thinking}>{ASK_THINKING_TEXT}</p>;
  }
  return (
    <div className={classes.answer}>
      {ask.answer.trim() === '' ? (
        <span>{ASK_NO_ANSWER_GIVEN_TEXT}</span>
      ) : (
        <AlphaMarkdown text={ask.answer} />
      )}
      {ask.phase === 'done' && <LiveAssistSources citations={ask.citations} />}
    </div>
  );
};

const LiveAssistAsk: React.FC<Props> = ({ asks, onAsk, inputRef, closedText }) => {
  const classes = useLiveAssistStyles();
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | undefined>();
  const fieldId = `la-ask-${useId().replace(/:/g, '')}`;
  const invalid = closedText === undefined && error !== undefined;
  const busy = asks.some(ask => OPEN_PHASES.includes(ask.phase));
  const closed = closedText !== undefined;
  const canSend = !busy && !closed;

  const send = (): void => {
    if (!canSend) return;
    const question = draft.trim();
    if (question === '') {
      setError(ASK_EMPTY_ERROR);
      return;
    }
    if (question.length > ASK_MAX_LENGTH) {
      setError(ASK_TOO_LONG_ERROR);
      return;
    }
    setError(undefined);
    onAsk(draft);
    setDraft('');
  };

  return (
    <>
      <div className={classes.panel}>
        {asks.length === 0 ? (
          closed ? null : (
            <p className={classes.empty}>{ASK_EMPTY_TEXT}</p>
          )
        ) : (
          <ol className={classes.askList} aria-label="Your questions">
            {asks.map(ask => (
              <li key={ask.id} className={classes.askItem}>
                <div className={classes.question}>{ask.question}</div>
                <AskAnswer
                  ask={ask}
                  onRetry={
                    closed || busy
                      ? undefined
                      : () => {
                          onAsk(ask.question, ask.id);
                        }
                  }
                />
              </li>
            ))}
          </ol>
        )}
      </div>
      <form
        className={classes.composer}
        onSubmit={event => {
          event.preventDefault();
          send();
        }}
      >
        <TextField
          className={classes.composerField}
          size="small"
          variant="outlined"
          label="Ask Alpha"
          multiline
          maxRows={3}
          value={draft}
          disabled={closed}
          id={fieldId}
          error={invalid}
          helperText={closedText ?? error}
          FormHelperTextProps={invalid ? { role: 'alert' } : undefined}
          inputRef={inputRef}
          onChange={event => {
            setDraft(event.target.value);
            setError(undefined);
          }}
          onKeyDown={event => {
            if (event.nativeEvent.isComposing || event.keyCode === IME_KEY_CODE) return;
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              send();
            }
          }}
        />
        <Button
          type="submit"
          variant="contained"
          disableElevation
          className={classes.sendButton}
          disabled={!canSend}
        >
          {busy ? 'Working' : 'Send'}
        </Button>
      </form>
    </>
  );
};

export default LiveAssistAsk;
